/**
 * Aritmetica de color: sRGB <-> OKLCH, contraste WCAG y ajuste a la gama.
 *
 * Puro y sin dependencias. Lo usan dos piezas:
 *
 * - `acento.ts`, que deriva la rampa de acento del color del consultorio.
 * - `hojas.ts`, que traduce los roles de `semantico.css` a sRGB para las pocas
 *   entradas de la paleta de MUI que MUI manipula con `alpha()`/`darken()`.
 *
 * Formulas de OKLab: Bjorn Ottosson (https://bottosson.github.io/posts/oklab/).
 * Contraste: WCAG 2.x (luminancia relativa de sRGB).
 */

export interface Oklch {
  /** Luminosidad percibida, 0..1. */
  l: number;
  /** Croma, 0..~0.37. */
  c: number;
  /** Tono en grados, 0..360. */
  h: number;
  /** Opacidad, 0..1. */
  alfa?: number;
}

/** sRGB lineal o gamma, canales 0..1. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
  alfa?: number;
}

const aLineal = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const aGamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

/** `#rgb` o `#rrggbb` (con o sin alfa) a sRGB 0..1. Devuelve `null` si no es hex valido. */
export function hexARgb(hex: string): Rgb | null {
  const limpio = hex.trim().replace(/^#/, '');
  if (!/^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(limpio)) return null;
  const largo = limpio.length <= 4 ? limpio.replace(/./g, (c) => c + c) : limpio;
  const canal = (i: number) => parseInt(largo.slice(i, i + 2), 16) / 255;
  return {
    r: canal(0),
    g: canal(2),
    b: canal(4),
    alfa: largo.length === 8 ? canal(6) : 1,
  };
}

export function rgbAHex({ r, g, b }: Rgb): string {
  const canal = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${canal(r)}${canal(g)}${canal(b)}`;
}

/** sRGB (gamma) a OKLCH. */
export function rgbAOklch({ r, g, b, alfa = 1 }: Rgb): Oklch {
  const lr = aLineal(r);
  const lg = aLineal(g);
  const lb = aLineal(b);
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const c = Math.hypot(A, B);
  let h = (Math.atan2(B, A) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h: c < 1e-4 ? 0 : h, alfa };
}

/** OKLCH a sRGB lineal SIN recortar (puede salir de 0..1 si esta fuera de gama). */
function oklchALineal({ l, c, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180;
  const A = c * Math.cos(rad);
  const B = c * Math.sin(rad);
  const l_ = l + 0.3963377774 * A + 0.2158037573 * B;
  const m_ = l - 0.1055613458 * A - 0.0638541728 * B;
  const s_ = l - 0.0894841775 * A - 1.291485548 * B;
  const L3 = l_ ** 3;
  const M3 = m_ ** 3;
  const S3 = s_ ** 3;
  return {
    r: 4.0767416621 * L3 - 3.3077115913 * M3 + 0.2309699292 * S3,
    g: -1.2684380046 * L3 + 2.6097574011 * M3 - 0.3413193965 * S3,
    b: -0.0041960863 * L3 - 0.7034186147 * M3 + 1.707614701 * S3,
  };
}

const EPS = 1e-5;

export function enGama(color: Oklch): boolean {
  const { r, g, b } = oklchALineal(color);
  return [r, g, b].every((v) => v >= -EPS && v <= 1 + EPS);
}

/**
 * Baja el croma, conservando luminosidad y tono, hasta que el color cabe en
 * sRGB. Es la forma de ajuste que menos cambia lo que se percibe.
 */
export function ajustarAGama(color: Oklch): Oklch {
  if (enGama(color)) return color;
  let bajo = 0;
  let alto = color.c;
  for (let i = 0; i < 24; i++) {
    const medio = (bajo + alto) / 2;
    if (enGama({ ...color, c: medio })) bajo = medio;
    else alto = medio;
  }
  return { ...color, c: bajo };
}

/** OKLCH a sRGB gamma 0..1 (recortado). */
export function oklchARgb(color: Oklch): Rgb {
  const { r, g, b } = oklchALineal(ajustarAGama(color));
  const recorte = (v: number) => aGamma(Math.min(1, Math.max(0, v)));
  return { r: recorte(r), g: recorte(g), b: recorte(b), alfa: color.alfa ?? 1 };
}

/** Luminancia relativa WCAG. */
export function luminancia({ r, g, b }: Rgb): number {
  return 0.2126 * aLineal(r) + 0.7152 * aLineal(g) + 0.0722 * aLineal(b);
}

/** Razon de contraste WCAG entre dos colores opacos. */
export function contraste(a: Rgb, b: Rgb): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Compone un color con alfa sobre un fondo opaco (en sRGB gamma, como el navegador). */
export function componer(frente: Rgb, fondo: Rgb): Rgb {
  const a = frente.alfa ?? 1;
  return {
    r: frente.r * a + fondo.r * (1 - a),
    g: frente.g * a + fondo.g * (1 - a),
    b: frente.b * a + fondo.b * (1 - a),
    alfa: 1,
  };
}

const redondear = (v: number, decimales: number) => {
  const f = 10 ** decimales;
  return Math.round(v * f) / f;
};

/** Formato CSS: `oklch(40% 0.076 162)` o `oklch(54.8% 0.109 160 / 0.18)`. */
export function formatoOklch({ l, c, h, alfa = 1 }: Oklch): string {
  const base = `${redondear(l * 100, 1)}% ${redondear(c, 3)} ${redondear(h, 1)}`;
  return alfa < 1 ? `oklch(${base} / ${redondear(alfa, 3)})` : `oklch(${base})`;
}

/**
 * Lee un color CSS de los que usan nuestras hojas: `oklch(...)`, `#hex`,
 * `transparent`. Devuelve `null` para cualquier otra cosa (p. ej. `color-mix`).
 */
export function leerColor(texto: string): Rgb | null {
  const valor = texto.trim();
  if (valor === 'transparent') return { r: 0, g: 0, b: 0, alfa: 0 };
  if (valor.startsWith('#')) return hexARgb(valor);
  const m =
    /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:deg)?\s*(?:\/\s*([\d.]+)(%?))?\s*\)$/i.exec(
      valor,
    );
  if (!m) return null;
  const l = Number(m[1]) / (m[2] ? 100 : 1);
  const alfa = m[5] === undefined ? 1 : Number(m[5]) / (m[6] ? 100 : 1);
  return oklchARgb({ l, c: Number(m[3]), h: Number(m[4]), alfa });
}

/** sRGB a la forma que MUI sabe manipular: `#rrggbb` o `rgba(r, g, b, a)`. */
export function formatoMui(color: Rgb): string {
  const a = color.alfa ?? 1;
  if (a >= 1) return rgbAHex(color);
  const c = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return `rgba(${c(color.r)}, ${c(color.g)}, ${c(color.b)}, ${redondear(a, 3)})`;
}
